import { PageLoader } from '@customerio/pluma-components/react';
import NotFoundPage from '@/pages/not-found.page';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useTemplateOptional } from '@/services/db/template/queries';
import type { Template } from '@/services/db/template/entity';
import { generatePath } from '@/services/router/generate-path';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { LegacyRedirectReporter } from './-legacy-redirects';
import { LastWorkspaceResolver } from './-route-layouts';

// Hydra port of the Ember `template` route (FPS-2300). `/template/:templateId`
// is a UI-less dispatcher: it loads the template and bounces to the editor
// that owns it. All three editors are still Ember-owned, so every branch is a
// SafeNavigate handoff to the embedded Journeys app.
function getTemplateEditorPath(workspaceId: number, template: Template): string | null {
  if (template.action_id != null) {
    return generatePath('/workspaces/:workspaceId/journeys/composer/actions/:actionId', {
      workspaceId,
      actionId: template.action_id,
    });
  }

  if (template.newsletter_id != null) {
    return generatePath('/workspaces/:workspaceId/journeys/composer/newsletters/:newsletterId/templates/:templateId', {
      workspaceId,
      newsletterId: template.newsletter_id,
      templateId: template.id,
    });
  }

  if (template.transactional_message_id != null) {
    return generatePath(
      '/workspaces/:workspaceId/journeys/composer/transactional/:transactionalMessageId/templates/:templateId',
      {
        workspaceId,
        transactionalMessageId: template.transactional_message_id,
        templateId: template.id,
      },
    );
  }

  return null;
}

function getJourneysDashboardPath(workspaceId: number): string {
  return generatePath('/workspaces/:workspaceId/journeys/dashboard', { workspaceId });
}

type TemplateDispatchProps = {
  templateId: string;
};

function TemplateDispatch({ templateId: templateIdParam }: TemplateDispatchProps) {
  const workspaceId = requireWorkspaceId();
  const templateId = Number(templateIdParam);
  const isValidTemplateId = Number.isInteger(templateId) && templateId > 0;
  const template = useTemplateOptional({ workspaceId, templateId, enabled: isValidTemplateId });

  if (!isValidTemplateId || template.isError) {
    return <NotFoundPage />;
  }

  if (template.data == null) {
    return <PageLoader />;
  }

  // Ember's dispatcher had no fallback branch — a template with no owning
  // editor dead-ended on a blank page. Bounce to the Journeys dashboard
  // instead, matching the bare `/template` behavior.
  const target = getTemplateEditorPath(workspaceId, template.data) ?? getJourneysDashboardPath(workspaceId);

  return <SafeNavigate to={target} options={{ shouldReplaceHistoryEntry: true }} />;
}

type TemplateDispatchRouteProps = {
  templateId: string;
};

export function TemplateDispatchRoute({ templateId }: TemplateDispatchRouteProps) {
  return (
    <LastWorkspaceResolver>
      <LegacyRedirectReporter>
        <TemplateDispatch templateId={templateId} />
      </LegacyRedirectReporter>
    </LastWorkspaceResolver>
  );
}

function TemplateIndexRedirect() {
  const workspaceId = requireWorkspaceId();

  // Ember `template-redirect`: the bare `/template` URL always bounced to the
  // dashboard.
  return <SafeNavigate to={getJourneysDashboardPath(workspaceId)} options={{ shouldReplaceHistoryEntry: true }} />;
}

export function TemplateIndexRedirectRoute() {
  return (
    <LastWorkspaceResolver>
      <LegacyRedirectReporter>
        <TemplateIndexRedirect />
      </LegacyRedirectReporter>
    </LastWorkspaceResolver>
  );
}
